import axios from "axios";
import React, { useMemo } from "react";
import { connect } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import Card from "../../../components/core/basic/Card";
import { Table } from "../../../components/table/Table";
import { RT8Pagination } from "../../../components/table/Pagination";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";

const URL_PREFIX = getURLPrefix();

const columnHelper = createAppColumnHelper();

function InvoiceTable({ data }) {
  const { t } = useTranslation(["admin"]);
  const columns = useMemo(
    () => [
      columnHelper.accessor("date", {
        header: t(($) => $.admin["Invoice Date"]),
        size: 120,
      }),
      columnHelper.accessor("subscription", {
        header: t(($) => $.admin["Subscription"]),
        size: 120,
      }),
      columnHelper.accessor("renewal", {
        header: t(($) => $.admin["Renewal"]),
        size: 100,
        cell: ({ getValue }) => <span className={css({ textTransform: "capitalize" })}>{getValue()}</span>,
      }),
      columnHelper.accessor("amount", {
        header: t(($) => $.admin["Total"]),
        size: 220,
      }),
      columnHelper.accessor("link", {
        header: t(($) => $.admin["View Invoice"]),
        size: 140,
        cell: ({ getValue }) => {
          const link = getValue();
          return link ?
              <a href={link} className={css({ textDecoration: "underline" })}>
                {t(($) => $.admin["View Full Invoice"])}
              </a>
            : t(($) => $.admin["Upcoming"]);
        },
      }),
    ],
    [t],
  );

  const table = useClientTable({
    data,
    columns,
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    enableSorting: false,
    enableColumnResizing: false,
  });

  return (
    <>
      <Table table={table} enableSelectAll={false} />
      <RT8Pagination table={table} canSetPageSize={false} />
    </>
  );
}

class Invoice extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      subList: "",
      invoiceList: "",
      upcomingInvoice: "",
    };
  }

  componentDidMount() {
    if (
      this.props.amz_onboard.org_status === "trial_active" ||
      this.props.amz_onboard.org_status === "trial_expired" ||
      this.props.amz_onboard.org_status === "pre_trial" ||
      this.props.amz_onboard.org_status === "qore_active"
    ) {
      // do nothing
    } else {
      this.getInvoiceList();
    }
  }

  getInvoiceList = () => {
    axios.get(URL_PREFIX + "/api/billing/invoices").then((res) => {
      if (res.data.past_invoices) {
        this.setState({ invoiceList: res.data.past_invoices.data });
      }
      if (res.data.upcoming_invoice) {
        this.setState({ upcomingInvoice: res.data.upcoming_invoice });
      }
    });
  };

  timeFormatter = (unixTime) => {
    const convertedTime = new Date(unixTime * 1000);
    return convertedTime.getUTCMonth() + 1 + "/" + convertedTime.getUTCDate() + "/" + convertedTime.getUTCFullYear();
  };

  subscriptionFormatter = (invoice) => {
    const { t } = this.props;
    for (let i = 0; i < invoice.lines.data.length; i++) {
      if (invoice.lines.data[i].description !== null) {
        if (invoice.lines.data[i].description.includes("ETP")) {
          return t(($) => $.admin["Essentials"]);
        } else if (invoice.lines.data[i].description.includes("PRO")) {
          return t(($) => $.admin["Premium"]);
        } else if (invoice.lines.data[i].description.includes("BUS")) {
          return t(($) => $.admin["Starter"]);
        } else if (invoice.lines.data[i].description.includes("ENT")) {
          return t(($) => $.admin["Enterprise"]);
        } else if (invoice.lines.data[i].description.includes("COR")) {
          return t(($) => $.admin["Custom"]);
        }
      }
    }

    const stripeDescription = invoice.lines.data[0];
    if (stripeDescription.description !== null) {
      if (stripeDescription.description.includes("ETP")) {
        return t(($) => $.admin["Entrepreneur"]);
      } else if (stripeDescription.description.includes("PRO")) {
        return t(($) => $.admin["Professional"]);
      } else if (stripeDescription.description.includes("BUS")) {
        return t(($) => $.admin["Business"]);
      } else if (stripeDescription.description.includes("ENT")) {
        return t(($) => $.admin["Enterprise"]);
      }
    }
  };

  lineItemFormatter = (invoice) => {
    const { t } = this.props;
    for (let i = 0; i < invoice.lines.data.length; i++) {
      if (invoice.lines.data[i].plan !== null) {
        const interval = invoice.lines.data[i].plan.interval;
        if (interval === "month") return t(($) => $.admin["monthly"]);
        if (interval === "year") return t(($) => $.admin["yearly"]);
        if (interval === "week") return t(($) => $.admin["weekly"]);
        if (interval === "day") return t(($) => $.admin["daily"]);
        return interval.concat("ly");
      }
    }
  };

  buildRows() {
    const { t } = this.props;
    const rows = [];

    if (this.state.upcomingInvoice !== "") {
      const inv = this.state.upcomingInvoice;
      rows.push({
        id: "upcoming",
        date: this.timeFormatter(inv.period_end),
        subscription: this.subscriptionFormatter(inv),
        renewal: this.lineItemFormatter(inv),
        amount: t(($) => $.admin["${amount} to be paid on {date}"], {
          amount: inv.total / 100,
          date: this.timeFormatter(inv.period_end),
        }),
        link: undefined,
      });
    }

    if (this.state.invoiceList !== "") {
      for (const invoiceItem of this.state.invoiceList) {
        rows.push({
          id: invoiceItem.id,
          date: this.timeFormatter(invoiceItem.period_start),
          subscription: this.subscriptionFormatter(invoiceItem),
          renewal: this.lineItemFormatter(invoiceItem),
          amount: t(($) => $.admin["${amount} to be paid on {date}"], {
            amount: invoiceItem.total / 100,
            date: this.timeFormatter(invoiceItem.period_end),
          }),
          link: invoiceItem.invoice_pdf,
        });
      }
    }

    return rows;
  }

  render() {
    const { t } = this.props;
    const rows = this.buildRows();

    return (
      <Card className={css({ paddingX: "large" })}>
        <VStack gap="medium" alignItems="stretch">
          <p className={css({ textAlign: "center", fontSize: "20px", fontWeight: 600 })}>
            {t(($) => $.admin["Invoice"])}
          </p>
          <InvoiceTable data={rows} />
        </VStack>
      </Card>
    );
  }
}

const mapStateToProps = (state) => ({
  amz_onboard: state.amz_onboard,
});

export default connect(mapStateToProps)(withTranslation(["admin"])(Invoice));
